import { useState, useEffect } from "react";
import {
  useGetFormalityRequestLogs,
  type Person,
} from "@/queries/formaly-request";
import SortDropdown from "@/pages/procesos/components/SortDropdown";
import Filter from "@/components/ui/filters";
import { Skeleton } from "@/components/ui/skeleton";
import ProcedureLogs from "./procedure-logs";
import type { LogItemType } from "@/types/common/logs";
import TablePagination from "@/components/common/table/pagination";

import type {
  SortOptionsTypes,
  SortDirectionType,
  FilterTypeList,
  CurrentFiltersType,
  MultiEntitySelections,
} from "@/types/common/filters";
import {
  sortDirectionEnum,
  filterTypesEnum,
  searchTypesEnum,
} from "@/types/common/filters";

import { useGetUsersAsInfinite } from "@/queries/users/users.api";
import type { FORM_ASSIGNMENTS_STATUS } from "@/types/entities/formality-request";
import { buildTimeline } from "./utils/serialization";

type Props = {
  formalityId: string;
};

export type FormalityRevisionApiItem = {
  id: string | number;
  status: "APPROVED" | "IN_PROCESS" | "DENIED" | string;
  applicant_form_assignment_id?: string | number | null;
  created_by?: string | number | null;
  updated_at?: Date | string | null;
  created_at?: Date | string | null;
  applicant_form_assignment?: {
    status: keyof typeof FORM_ASSIGNMENTS_STATUS;
  };
  corrections?: Array<{
    id: string | number;
    revision_id?: string | number;
    status: "APPROVED" | "IN_PROCESS" | "DENIED" | string;
    created_by_id?: string | number | null;
    created_by?: Person | null;
    created_at?: Date | string | null;
    updated_at?: Date | string | null;
    comments?: Array<{
      id: string | number;
      text?: string;
      created_by?: string | number | null;
      created_at?: Date | string | null;
      updated_at?: Date | string | null;
      creator?: Person | null;
    }> | null;
  }> | null;
};

const sortFields: SortOptionsTypes[] = [
  { label: "Todos", value: "ALL" },
  { label: "Aprobado", value: "APPROVED" },
  { label: "En proceso", value: "IN_PROCESS" },
  { label: "Denegado", value: "DENIED" },
];

const FilterFields: FilterTypeList[] = [
  {
    label: "Fecha de creación",
    type: filterTypesEnum.dates,
    field: "created_at",
  },
  {
    label: "Creador",
    type: filterTypesEnum.textsearch,
    field: "created_by",
    searchField: "search",
    query: useGetUsersAsInfinite,
    searchType: searchTypesEnum.user,
  },
];

export const History = ({ formalityId }: Props) => {
  const [filters, setFilters] = useState<CurrentFiltersType[]>([]);
  const [selectedEntities, setSelectedEntities] =
    useState<MultiEntitySelections>({});
  const [orderDirection, setOrderDirection] = useState<SortDirectionType>(
    sortDirectionEnum.desc,
  );
  const [selectedStatus, setSelectedStatus] = useState<
    "APPROVED" | "IN_PROCESS" | "DENIED" | undefined
  >(undefined);

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(50);
  const [allLogs, setAllLogs] = useState<LogItemType[]>([]);

  const {
    data: logsData,
    isLoading: logsIsLoading,
    isError: logsIsError,
  } = useGetFormalityRequestLogs(String(formalityId), {
    page: currentPage,
    per_page: perPage,
    grouped: 1,
    order_by:
      orderDirection === sortDirectionEnum.desc ? "!created_at" : "created_at",
  });

  useEffect(() => {
    if (!logsData?.data) return;

    // logsData.data es el array de logs
    const logsArray = Array.isArray(logsData.data) ? logsData.data : [];

    const logs = buildTimeline(logsArray);
    const suspensionExtensionEvents = new Set([
      "node_time_extension_request",
      "node_deadline_suspension_request",
      "suspension_request_created_external",
      "suspension_request_created_internal",
      "suspension_request_approved",
      "suspension_request_denied",
      "suspension_request_scaled",
      "suspension_request_analyst_approved",
      "suspension_request_analyst_denied",
      "suspension_request_leader_approved",
      "suspension_request_leader_denied",
      "extension_request_created",
      "extension_request_approved",
      "extension_request_denied",
      "extension_request_scaled",
      "extension_request_analyst_approved",
      "extension_request_analyst_denied",
      "extension_request_leader_approved",
      "extension_request_leader_denied",
    ]);

    const normalizeEvent = (
      eventValue?: string,
      actionValue?: string,
    ): string | undefined => {
      const source = eventValue || actionValue;
      if (!source) return undefined;
      return source.trim().toLowerCase();
    };

    const formatted: LogItemType[] = logs.map(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (log: any, idx: number) => {
        const normalizedEvent = normalizeEvent(
          log?.event,
          log?.properties?.action,
        );

        let authorName = "Sistema";
        if (log?.causer) {
          // Los usuarios (type: "user") tienen "name" y "last_name"
          // Los solicitantes (type: "applicant") tienen "first_name" y "last_name"
          const firstName = log.causer.first_name || log.causer.name || "";
          const lastName = log.causer.last_name || "";
          const fullName = `${firstName} ${lastName}`.trim();
          authorName = fullName || log.causer.email || "Sistema";
        }

        let description = "";
        let action = log?.description ?? normalizedEvent ?? log?.event ?? "";

        if (normalizedEvent && suspensionExtensionEvents.has(normalizedEvent)) {
          description =
            log?.properties?.action_label ||
            log?.description ||
            normalizedEvent ||
            "";
          action = normalizedEvent;
        } else if (normalizedEvent === "group_reassignment") {
          description = log?.description || "Reasignación de grupo";
          action = normalizedEvent;
        } else if (log?.subject_type === "formality_request") {
          const formalityName =
            log?.subject?.name || log?.properties?.attributes?.name || "";
          const formalityCode =
            log?.subject?.code || log?.properties?.attributes?.code || "";
          const formalityStatus =
            log?.subject?.status || log?.properties?.attributes?.status;

          description =
            formalityName || formalityCode || "Solicitud de trámite";

          // Si es creación, mejorar el mensaje
          if (action === "created") {
            if (formalityStatus === "SUSPENSION_REQUEST") {
              description = "Solicitud de suspensión creada";
              action = "";
            } else if (formalityStatus === "EXTENSION_REQUEST") {
              description = "Solicitud de prórroga creada";
              action = "";
            } else {
              action = "formality_request creado";
            }
          }
        } else if (log?.subject_type === "filled_form") {
          // Para formularios completados
          const oldStatus = log?.properties?.old?.status;
          const newStatus = log?.properties?.attributes?.status;

          if (oldStatus && newStatus && oldStatus !== newStatus) {
            description = "Formulario actualizado";
          } else if (newStatus === "completed") {
            description = "Formulario completado";
          } else if (newStatus === "in_progress") {
            description = "Formulario en progreso";
          } else {
            description = "Formulario actualizado";
          }

          if (action === "created") {
            description = "Formulario creado";
          }
        } else if (log?.subject_type === "revision") {
          // Para revisiones
          description =
            log?.properties?.action_label ||
            `Revisión ${log?.subject?.status || "iniciada"}`;

          if (normalizedEvent === "revision_created") {
            action = "Revision started";
          }
        } else if (log?.subject_type === "correction") {
          // Para correcciones
          description =
            log?.properties?.action_label || log?.description || "Corrección";

          if (normalizedEvent === "correction_created") {
            action = "correction_created";
          }
        } else {
          // Fallback genérico - priorizar description y action_label
          description =
            log?.description ||
            log?.properties?.action_label ||
            log?.subject?.name ||
            log?.subject?.code ||
            log?.properties?.attributes?.name ||
            log?.properties?.old?.name ||
            log?.subject_type ||
            "Actividad";
        }

        if (log.revision) {
          description = "Revisión";
          authorName =
            `${log?.internal_user?.name || ""} ${log?.internal_user?.last_name || ""}`.trim() ||
            authorName;
        }

        return {
          id: Number(log.id ?? idx),
          description,
          action,
          author: authorName,
          date: log?.created_at ? new Date(log.created_at) : new Date(),
          subject_type: log?.subject_type,
          subject_id: log?.subject_id,
          subject: log?.subject ?? null,
          properties: log?.properties ?? {},
          revisions: log?.revisions ?? [],
          logs: log?.logs ?? [],
          event: normalizedEvent,
          causer_type: log?.causer_type ?? undefined,
        };
      },
    );

    setAllLogs(formatted);
  }, [logsData]);

  const handleApplySort = (
    field: string | undefined,
    direction: SortDirectionType,
  ) => {
    setOrderDirection(direction);

    if (!field || field === "ALL") {
      setSelectedStatus(undefined);
      return;
    }

    if (field === "APPROVED" || field === "IN_PROCESS" || field === "DENIED") {
      setSelectedStatus(field);
    } else {
      setSelectedStatus(undefined);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleNext = () => {
    if (logsData?.meta && currentPage < logsData.meta.last_page) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <>
      <div className="flex justify-between pb-6" data-testid="process-history">
        <div className="flex space-x-3">
          <SortDropdown
            currentOrderField={selectedStatus ?? "ALL"}
            currentOrderDirection={orderDirection}
            sortOptions={sortFields}
            onClick={handleApplySort}
          />
          <Filter
            filters={FilterFields}
            currentFilters={filters}
            onCurrentFiltersChange={setFilters}
            selecteds={selectedEntities}
            onSetSelecteds={setSelectedEntities}
          />
        </div>
      </div>

      <div className="bg-neutral-object border border-neutral-quiet rounded p-4 h-[calc(100vh-370px)] overflow-auto">
        {logsIsLoading ? (
          <div role="status" className="max-w-sm animate-pulse">
            <Skeleton className="h-3 rounded-full w-48 mb-4" />
            <Skeleton className="h-2 rounded-full max-w-95 mb-2.5" />
            <Skeleton className="h-2 rounded-full max-w-85 mb-2.5" />
            <Skeleton className="h-2 rounded-full max-w-[320px] mb-2.5" />
          </div>
        ) : logsIsError ? (
          <p className="text-danger">Error al cargar registros</p>
        ) : (
          <ProcedureLogs
            title=""
            items={allLogs}
            emptyText="Sin actividad registrada"
            formalityId={formalityId}
          />
        )}
      </div>

      {/* Paginación */}
      {logsData?.meta && logsData.meta.total > 0 && (
        <div className="mt-4">
          <TablePagination
            currentPage={currentPage}
            totalPages={logsData.meta.last_page}
            totalItems={logsData.meta.total}
            pageSize={perPage}
            onPageChange={handlePageChange}
            onNext={handleNext}
            onPrevious={handlePrevious}
            hasNext={currentPage < logsData.meta.last_page}
            hasPrevious={currentPage > 1}
          />
        </div>
      )}
    </>
  );
};

// checkpoint
